import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Printer } from 'lucide-react';
import { socket } from '../Containers/MainContainer';
import Skeleton from 'react-loading-skeleton';

          
const numberWithCommas = (num) => num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

// Shared sub-components
const Button = ({ variant = "primary", size = "md", onClick, children, disabled = false }) => {
  const variants = {
    primary: { background: '#238636', color: '#fff', border: '1px solid #2ea043' },
    secondary: { background: '#21262d', color: '#c9d1d9', border: '1px solid #30363d' },
    success: { background: '#238636', color: '#fff', border: '1px solid #2ea043' },
    danger: { background: '#da3633', color: '#fff', border: '1px solid #f85149' },
  };
  const sizes = { sm: { padding: '6px 12px', fontSize: '12px' }, md: { padding: '8px 16px', fontSize: '14px' } };
  return (
    <button onClick={onClick} disabled={disabled} style={{
      borderRadius: '8px', fontWeight: 500, cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1, transition: 'all 0.2s', display: 'inline-flex',
      alignItems: 'center', justifyContent: 'center', ...variants[variant], ...sizes[size],
    }}>{children}</button>
  );
};

const Card = ({ title, children, headerActions }) => (
  <div style={{ background: '#161b22', borderRadius: '12px', border: '1px solid #21262d', overflow: 'hidden' }}>
    {title && (
      <div style={{ padding: '16px', borderBottom: '1px solid #21262d', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#e6edf3', margin: 0 }}>{title}</h3>
        {headerActions}
      </div>
    )}
    <div style={{ padding: '16px' }}>{children}</div>
  </div>
);

const selectStyle = {
  padding: '8px 16px', background: '#0d1117', border: '1px solid #30363d',
  borderRadius: '8px', color: '#e6edf3', fontSize: '14px', cursor: 'pointer',
};

const monthNames = [
  "Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie",
  "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie"
];

const getExplorerUrl = (crypto, txId) => {
  const explorers = {
    'BTC': `https://blockchain.com/btc/tx/${txId}`,
    'ETH': `https://etherscan.io/tx/${txId}`,
    'USDT': `https://etherscan.io/tx/${txId}`,
    'ADA': `https://cardanoscan.io/transaction/${txId}`,
    'SOL': `https://solscan.io/tx/${txId}`,
    'BNB': `https://bscscan.com/tx/${txId}`,
    'XRP': `https://xrpscan.com/tx/${txId}`,
    'MATIC': `https://polygonscan.com/tx/${txId}`,
    'DOT': `https://polkadot.subscan.io/extrinsic/${txId}`,
    'AVAX': `https://snowtrace.io/tx/${txId}`,
    'TRX': `https://tronscan.org/#/transaction/${txId}`,
    'LTC': `https://blockchair.com/litecoin/transaction/${txId}`,
    'ATOM': `https://www.mintscan.io/cosmos/txs/${txId}`,
    'LINK': `https://etherscan.io/tx/${txId}`,
    'UNI': `https://etherscan.io/tx/${txId}`,
  };
  return explorers[crypto] || `#${txId}`;
};

// ========== EXPORT HELPERS ==========

const sanitizeFilename = (text) => text.replace(/\s+/g, '_');

const buildExportCSV = (transactions, totals, yearText, monthText) => {
  const headers = ['Data', 'ID Utilizator', 'Adresa/Email', 'Transaction ID', 'Simbol', 'Cantitate', 'Observatii'];
  const csvContent = [
    headers.join(','),
    ...transactions.map(tx => [
      tx.data, tx.userId, `"${tx.address}"`, `"${tx.txId}"`, tx.crypto, tx.cantitate, `"${tx.observatii}"`
    ].join(','))
  ].join('\n');

  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `Tranzactii_Platforma_${sanitizeFilename(yearText)}_${sanitizeFilename(monthText)}_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
};

const buildExportHTML = (transactions, totals, yearText, monthText) => {
  const totalsHTML = Object.entries(totals)
    .map(([crypto, total]) => `<span style="display: inline-block; background: #f3f4f6; padding: 8px 16px; margin: 4px; border-radius: 6px; border: 1px solid #d1d5db;"><strong>${crypto}:</strong> ${total.toFixed(6)}</span>`).join('');

  const tableRows = transactions.map(tx => {
    const explorerUrl = getExplorerUrl(tx.crypto, tx.txId);
    return `<tr><td>${tx.data}</td><td>${tx.userId}</td><td>${tx.address}</td><td><a href="${explorerUrl}" target="_blank">${tx.txId}</a></td><td>${tx.crypto}</td><td>${tx.cantitate.toFixed(6)}</td><td>${tx.observatii}</td></tr>`;
  }).join('');

  const htmlContent = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Tranzactii Platforma - ${yearText} - ${monthText}</title>
<style>body{font-family:Arial,sans-serif;margin:20px}h1{color:#1f2937}.totals{margin:20px 0;padding:15px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px}table{width:100%;border-collapse:collapse;margin-top:20px}th{background:#1f2937;color:white;padding:12px;text-align:left}td{padding:10px;border:1px solid #e5e7eb}tr:nth-child(even){background:#f9fafb}</style></head>
<body><h1>Tranzactii Platforma</h1><p>Perioada: ${yearText} - ${monthText}</p>
<div class="totals"><strong>Total Cantitati:</strong><br>${totalsHTML}<br><strong>Total tranzactii:</strong> ${numberWithCommas(transactions.length)}</div>
<table><thead><tr><th>Data</th><th>ID Utilizator</th><th>Adresa/Email</th><th>Transaction ID</th><th>Simbol</th><th>Cantitate</th><th>Observatii</th></tr></thead><tbody>${tableRows}</tbody></table>
<p style="margin-top:20px;text-align:center;color:#6b7280;font-size:12px;">Generat la: ${new Date().toLocaleString('ro-RO')}</p></body></html>`;

  const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `Tranzactii_Platforma_${sanitizeFilename(yearText)}_${sanitizeFilename(monthText)}_${new Date().toISOString().split('T')[0]}.html`;
  link.click();
  URL.revokeObjectURL(link.href);
};

const buildPrint = (transactions, totals, yearText, monthText) => {
  const loadingMsg = document.createElement('div');
  loadingMsg.style.cssText = 'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);background:rgba(0,0,0,0.9);color:white;padding:30px 50px;border-radius:10px;z-index:10000;font-size:18px;';
  loadingMsg.textContent = 'Se pregateste documentul pentru printare...';
  document.body.appendChild(loadingMsg);

  setTimeout(() => {
    try {
      const printWindow = window.open('', '_blank');
      if (!printWindow) { document.body.removeChild(loadingMsg); alert('Va rugam sa permiteti pop-up-urile.'); return; }

      const totalsHTML = Object.entries(totals)
        .map(([crypto, total]) => `<span style="display:inline-block;background:#f3f4f6;padding:4px 10px;margin:2px;border-radius:4px;border:1px solid #ddd;font-size:10px;"><b>${crypto}:</b> ${total.toFixed(6)}</span>`).join('');

      printWindow.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Tranzactii - ${yearText} - ${monthText}</title>
<style>@page{size:A4 landscape;margin:8mm}*{margin:0;padding:0;box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:8px;color:#000;background:white}
.header{margin-bottom:10px;padding-bottom:6px;border-bottom:2px solid #000}.header h1{font-size:14px;margin-bottom:3px}.header .period{font-size:10px}
.totals{margin-bottom:10px;padding:6px;background:#f5f5f5;border:1px solid #ddd;font-size:9px}
table{width:100%;border-collapse:collapse;font-size:8px}thead{background:#000;color:white}th{padding:4px 2px;text-align:left;font-weight:bold;font-size:8px;border:1px solid #000}
td{padding:2px;border:1px solid #ccc;word-break:break-word}tbody tr{page-break-inside:avoid}tbody tr:nth-child(even){background:#f9f9f9}
.footer{margin-top:8px;padding-top:5px;border-top:1px solid #ddd;text-align:center;font-size:8px}
@media print{body{print-color-adjust:exact;-webkit-print-color-adjust:exact}thead{display:table-header-group}tr{page-break-inside:avoid}}</style></head>
<body><div class="header"><h1>Tranzactii Platforma</h1><div class="period">Perioada: ${yearText} - ${monthText}</div></div>
<div class="totals"><b>Total Cantitati:</b> ${totalsHTML}<br><b>Total tranzactii:</b> ${numberWithCommas(transactions.length)}</div>
<table><thead><tr><th style="width:6%">Data</th><th style="width:8%">ID</th><th style="width:22%">Adresa</th><th style="width:20%">TX ID</th><th style="width:5%">Simbol</th><th style="width:8%">Cantitate</th><th style="width:15%">Observatii</th></tr></thead>
<tbody id="tableBody"></tbody></table>
<div class="footer">Generat: ${new Date().toLocaleString('ro-RO')} | ${numberWithCommas(transactions.length)} tranzactii</div></body></html>`);
      printWindow.document.close();

      const tbody = printWindow.document.getElementById('tableBody');
      const chunkSize = 500;
      let idx = 0;
      const addChunk = () => {
        try {
          const end = Math.min(idx + chunkSize, transactions.length);
          const frag = printWindow.document.createDocumentFragment();
          for (let i = idx; i < end; i++) {
            const tx = transactions[i];
            const tr = printWindow.document.createElement('tr');
            tr.innerHTML = `<td style="font-size:8px">${tx.data}</td><td style="font-size:8px">${tx.userId}</td><td style="font-size:7px;font-family:monospace;word-break:break-all">${tx.address}</td><td style="font-size:7px;font-family:monospace;word-break:break-all">${tx.txId}</td><td style="font-size:8px;font-weight:bold;text-align:center">${tx.crypto}</td><td style="font-size:8px;font-family:monospace;text-align:right">${tx.cantitate.toFixed(6)}</td><td style="font-size:8px">${tx.observatii}</td>`;
            frag.appendChild(tr);
          }
          tbody.appendChild(frag);
          idx = end;
          if (idx < transactions.length) {
            loadingMsg.textContent = `Se pregateste... ${Math.round((idx / transactions.length) * 100)}%`;
            setTimeout(addChunk, 5);
          } else {
            document.body.removeChild(loadingMsg);
            printWindow.focus();
            setTimeout(() => printWindow.print(), 300);
          }
        } catch { document.body.removeChild(loadingMsg); alert('Eroare la generarea documentului.'); }
      };
      addChunk();
    } catch {
      if (loadingMsg.parentNode) document.body.removeChild(loadingMsg);
      alert('Eroare la deschiderea ferestrei de printare.');
    }
  }, 100);
};

// ========== MAIN COMPONENT ==========

const CryptoTransactionTable = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [jumpPage, setJumpPage] = useState('');

  // Server-provided aggregates
  const [totalPages, setTotalPages] = useState(0);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [totals, setTotals] = useState({});          // { BTC: 1.23, ETH: 5.67, ... }
  const [availableYears, setAvailableYears] = useState([]);
  const [cryptoCount, setCryptoCount] = useState(0);

  const exportTypeRef = useRef(null);
  const exportYearRef = useRef(selectedYear);
  const exportMonthRef = useRef(selectedMonth);

  // Fetch paginated data
  const fetchData = (p = 1) => {
    setLoading(true);
    socket.emit('getAccountingTransactions', {
      page: p,
      itemsPerPage,
      year: selectedYear,
      month: selectedMonth,
    });
  };

  useEffect(() => {
    fetchData(1);

    // Timeout fallback if backend hasn't implemented this yet
    const timeout = setTimeout(() => setLoading(false), 5000);

    socket.on('response_get_accounting_transactions', (res) => {
      clearTimeout(timeout);
      const d = res.data || res;
      setTransactions(d.transactions || []);
      setTotalPages(d.pagination?.totalPages || 0);
      setTotalTransactions(d.pagination?.totalTransactions || 0);
      setTotals(d.totals || {});
      setAvailableYears(d.availableYears || []);
      setCryptoCount(d.cryptoCount || 0);
      setLoading(false);
    });

    socket.on('response_get_accounting_transactions_export', (res) => {
      const d = res.data || res;
      const type = exportTypeRef.current;
      const y = exportYearRef.current;
      const m = exportMonthRef.current;
      const yearText = y === 'all' ? 'Toti anii' : y;
      const monthText = m === 'all' ? 'Toate lunile' : monthNames[parseInt(m) - 1];

      if (type === 'csv') buildExportCSV(d.transactions || [], d.totals || {}, yearText, monthText);
      else if (type === 'html') buildExportHTML(d.transactions || [], d.totals || {}, yearText, monthText);
      else if (type === 'print') buildPrint(d.transactions || [], d.totals || {}, yearText, monthText);

      exportTypeRef.current = null;
      setExporting(false);
    });

    return () => {
      clearTimeout(timeout);
      socket.off('response_get_accounting_transactions');
      socket.off('response_get_accounting_transactions_export');
    };
  }, []);

  // Re-fetch when filters change
  useEffect(() => {
    fetchData(1);
    setPage(1);
  }, [selectedYear, selectedMonth, itemsPerPage]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchData(newPage);
  };

  const handleJumpToPage = () => {
    const n = parseInt(jumpPage);
    if (n && n > 0 && n <= totalPages) { handlePageChange(n); setJumpPage(''); }
  };

  const requestExport = (type) => {
    exportTypeRef.current = type;
    exportYearRef.current = selectedYear;
    exportMonthRef.current = selectedMonth;
    setExporting(true);
    socket.emit('getAccountingTransactionsExport', {
      year: selectedYear,
      month: selectedMonth,
    });
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0d1117' }}>
      <main style={{ maxWidth: '1280px', margin: '0 auto', padding: '32px 16px', display: 'flex', flexDirection: 'column', gap: '24px' }}>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
          {[
            { title: 'Total Tranzactii', value: numberWithCommas(totalTransactions), color: 'blue' },
            { title: 'Criptomonede', value: cryptoCount, color: 'green' },
            { title: 'Perioada', value: selectedYear === 'all' ? 'Toti anii' : selectedYear, color: 'purple' },
          ].map((s, i) => (
            <div key={i} className={`metric-banner metric-banner--${s.color}`}>
              {loading ? <Skeleton height={40} baseColor="#161b22" highlightColor="#21262d" /> : (
                <div className="metric-banner-row">
                  <div className="stat-label">{s.title}</div>
                  <div className={`metric-banner-value metric-banner-value--${s.color}`}>{s.value}</div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Filters & Export */}
        <Card title="Filtrare si Export">
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Calendar style={{ width: '20px', height: '20px', color: '#8b949e' }} />
              <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} style={selectStyle}>
                <option value="all">Toti anii</option>
                {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>

            <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} style={selectStyle}>
              <option value="all">Toate lunile</option>
              {monthNames.map((m, i) => <option key={i} value={String(i + 1)}>{m}</option>)}
            </select>

            <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setPage(1); }} style={selectStyle}>
              <option value={10}>10 pe pagina</option>
              <option value={25}>25 pe pagina</option>
              <option value={50}>50 pe pagina</option>
              <option value={100}>100 pe pagina</option>
            </select>

            <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
              <Button variant="secondary" onClick={() => requestExport('print')} disabled={exporting}>
                <Printer style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                {exporting && exportTypeRef.current === 'print' ? 'Loading...' : 'Print'}
              </Button>
              <Button variant="success" onClick={() => requestExport('csv')} disabled={exporting}>CSV</Button>
              <Button variant="success" onClick={() => requestExport('html')} disabled={exporting}>HTML</Button>
            </div>
          </div>
        </Card>

        {/* Totals per crypto */}
        <Card title="Total Cantitati pe Criptomoneda">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
            {loading ? (
              Array(3).fill().map((_, i) => <Skeleton key={i} width={140} height={44} baseColor="#161b22" highlightColor="#21262d" />)
            ) : (
              Object.entries(totals).map(([label, total]) => (
                <div key={label} style={{ background: '#0d1117', padding: '12px 16px', borderRadius: '8px', border: '1px solid #21262d' }}>
                  <span style={{ fontWeight: 700, color: '#58a6ff', fontSize: '14px' }}>{label}:</span>
                  <span style={{ marginLeft: '8px', color: '#e6edf3', fontFamily: 'monospace' }}>{typeof total === 'number' ? total.toFixed(2) : total}</span>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Data Table */}
        <Card
          title="Tranzactii Platforma"
          headerActions={<span style={{ fontSize: '13px', color: '#8b949e' }}>Pagina {page} din {totalPages} ({numberWithCommas(totalTransactions)} total)</span>}
        >
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#161b22' }}>
                  {['Data', 'ID Utilizator', 'Adresa/Email', 'Transaction ID', 'Simbol', 'Cantitate', 'Observatii'].map((h, i) => (
                    <th key={i} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: 500, color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.8px', borderBottom: '1px solid #21262d', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array(itemsPerPage > 10 ? 10 : itemsPerPage).fill().map((_, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #21262d' }}>
                      {Array(7).fill().map((_, j) => <td key={j} style={{ padding: '16px' }}><Skeleton height={16} baseColor="#161b22" highlightColor="#21262d" /></td>)}
                    </tr>
                  ))
                ) : (
                  transactions.map((tx, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #21262d', transition: 'background 0.15s' }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#1c2129'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '16px', whiteSpace: 'nowrap', fontSize: '14px', color: '#c9d1d9' }}>{tx.data}</td>
                      <td style={{ padding: '16px', whiteSpace: 'nowrap', fontSize: '14px', color: '#e6edf3' }}>{tx.userId}</td>
                      <td style={{ padding: '16px', fontSize: '12px', color: '#c9d1d9', fontFamily: 'monospace', maxWidth: '250px' }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={tx.address}>{tx.address}</div>
                      </td>
                      <td style={{ padding: '16px', fontSize: '12px', maxWidth: '250px' }}>
                        <a href={getExplorerUrl(tx.crypto, tx.txId)} target="_blank" rel="noopener noreferrer"
                          style={{ color: '#58a6ff', fontFamily: 'monospace', textDecoration: 'none' }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={tx.txId}>{tx.txId}</div>
                        </a>
                      </td>
                      <td style={{ padding: '16px', whiteSpace: 'nowrap' }}>
                        <span style={{ padding: '4px 10px', background: 'rgba(88,166,255,0.15)', color: '#58a6ff', borderRadius: '9999px', fontSize: '12px', fontWeight: 600 }}>{tx.crypto}</span>
                      </td>
                      <td style={{ padding: '16px', whiteSpace: 'nowrap', fontSize: '14px', color: '#3fb950', fontFamily: 'monospace', fontWeight: 500 }}>{typeof tx.cantitate === 'number' ? tx.cantitate.toFixed(6) : tx.cantitate}</td>
                      <td style={{ padding: '16px', fontSize: '14px', color: '#c9d1d9' }}>{tx.observatii}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                <Button variant="secondary" size="sm" onClick={() => handlePageChange(1)} disabled={page === 1}>First</Button>
                <Button variant="secondary" size="sm" onClick={() => handlePageChange(page - 1)} disabled={page === 1}>Prev</Button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                  return start + i;
                }).filter(p => p <= totalPages).map(p => (
                  <Button key={p} variant={p === page ? "primary" : "secondary"} size="sm" onClick={() => handlePageChange(p)}>{p}</Button>
                ))}
                <Button variant="secondary" size="sm" onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>Next</Button>
                <Button variant="secondary" size="sm" onClick={() => handlePageChange(totalPages)} disabled={page === totalPages}>Last</Button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="number" placeholder="Page" value={jumpPage} onChange={(e) => setJumpPage(e.target.value)}
                  style={{ ...selectStyle, width: '80px' }} />
                <Button variant="secondary" size="sm" onClick={handleJumpToPage}>Go</Button>
              </div>
            </div>
          )}

          {transactions.length === 0 && !loading && (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 500, color: '#e6edf3', marginBottom: '8px' }}>Nu exista tranzactii</h3>
              <p style={{ color: '#8b949e', fontSize: '14px' }}>Nu exista tranzactii pentru filtrele selectate.</p>
            </div>
          )}
        </Card>
      </main>
    </div>
  );
};

export default CryptoTransactionTable;
